import type { BusinessAddress } from '@/types';

export type BusinessAddressApiFields = {
  address?: string | null;
  addressJson?: unknown;
};

/**
 * Formats a business address object or string into a clean, human-readable string.
 * Handles cases where the address might be a JSON string or a structured object.
 */
export function formatBusinessAddress(
  address?: string | BusinessAddress | null,
  simplified = false
): string {
  if (!address) return '';

  let addrObj: string | BusinessAddress | Record<string, unknown> | undefined = address;

  // If it's a string, try to parse it in case it's stringified JSON
  if (typeof address === 'string') {
    if (address.trim().startsWith('{')) {
      try {
        addrObj = JSON.parse(address);
      } catch (_e) {
        // Not valid JSON, treat as a regular string
        return address;
      }
    } else {
      return address;
    }
  }

  // Handle structured object
  if (typeof addrObj === 'object' && addrObj !== null) {
    const obj = addrObj as Record<string, string | number | null | undefined>;
    if (simplified) {
      const city = obj.taluka || obj.city || '';
      const area = obj.addressLine2 || obj.landmark || '';
      return [area, city].filter(Boolean).join(', ');
    }

    const parts = [
      obj.addressLine1 || obj.line1,
      obj.addressLine2 || obj.line2,
      obj.landmark,
      obj.taluka || obj.city,
      obj.district,
      obj.state,
      obj.country,
      obj.pinCode || obj.pincode,
    ];

    return parts.filter(Boolean).join(', ');
  }

  return String(address);
}

export function addressJsonHasMeaningfulContent(addressJson: unknown): boolean {
  if (addressJson == null) return false;
  if (typeof addressJson !== 'object') return false;
  const o = addressJson as Record<string, unknown>;
  return Object.values(o).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    return true;
  });
}

export function displayBusinessAddressFields(
  fields: BusinessAddressApiFields,
  simplified = false
): string {
  if (addressJsonHasMeaningfulContent(fields.addressJson)) {
    return formatBusinessAddress(fields.addressJson as BusinessAddress, simplified);
  }
  if (fields.address) {
    return formatBusinessAddress(fields.address, simplified);
  }
  return '';
}

export function hasBusinessAddressForDisplay(fields: BusinessAddressApiFields): boolean {
  return displayBusinessAddressFields(fields, false).length > 0;
}

/** First available of city (or taluka), district, or state — for compact directory cards. */
export function formatBusinessLocationShort(fields: BusinessAddressApiFields): string {
  if (!addressJsonHasMeaningfulContent(fields.addressJson)) return '';

  const obj = fields.addressJson as Record<string, string | undefined>;
  const city = (obj.city || obj.taluka || '').trim();
  const district = (obj.district || '').trim();
  const state = (obj.state || '').trim();

  return city || district || state;
}
